'use client';

import { cn } from '../../lib/utils.js';
import { Section, SectionHeader } from '../layout/Section.jsx';
import { Card, CardBody, CardTitle, CardDescription } from '../ui/Card.jsx';
import { Icon } from '../ui/Icon.jsx';
import { FadeIn } from '../animation/FadeIn.jsx';

/**
 * Services Section component with grid layout
 */
export function ServicesSection({
  headline,
  subheadline,
  description,
  items = [],
  columns = 3,
  variant = 'default',
  className,
  renderLink,
}) {
  const Link = renderLink || 'a';

  const columnClasses = {
    2: 'grid-cols-1 md:grid-cols-2',
    3: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
    4: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4',
  };

  return (
    <Section variant={variant} className={className} id="services">
      <SectionHeader
        title={headline}
        subtitle={subheadline}
        description={description}
      />

      <div className={cn('grid gap-6 md:gap-8', columnClasses[columns])}>
        {items.map((item, index) => (
          <FadeIn key={index} delay={index * 0.1}>
            <ServiceCard item={item} Link={Link} />
          </FadeIn>
        ))}
      </div>
    </Section>
  );
}

/**
 * Service Card component
 */
function ServiceCard({ item, Link }) {
  const content = (
    <Card
      variant="default"
      className="group h-full hover:border-accent-dark dark:hover:border-accent"
      padding={false}
    >
      <CardBody className="p-8">
        {/* Icon */}
        {item.icon && (
          <div className="w-14 h-14 rounded-lg bg-accent/10 flex items-center justify-center mb-6 group-hover:bg-accent-dark dark:group-hover:bg-accent transition-colors duration-400">
            <Icon
              name={item.icon}
              size="lg"
              className="text-accent dark:text-accent group-hover:text-white transition-colors duration-400"
            />
          </div>
        )}

        {/* Title */}
        <CardTitle className="group-hover:text-accent dark:group-hover:text-accent transition-colors">
          {item.title}
        </CardTitle>

        {/* Description */}
        <CardDescription className="mt-3">
          {item.description}
        </CardDescription>

        {/* Link Arrow */}
        {item.href && (
          <div className="mt-6 flex items-center text-accent-dark dark:text-accent text-sm font-medium">
            <span>Mehr erfahren</span>
            <Icon
              name="arrowRight"
              size="sm"
              className="ml-2 transition-transform group-hover:translate-x-1"
            />
          </div>
        )}
      </CardBody>
    </Card>
  );

  if (item.href) {
    return (
      <Link href={item.href} className="block h-full">
        {content}
      </Link>
    );
  }

  return content;
}

export default ServicesSection;
